import { h, Fragment } from 'preact'
import { useState, useEffect, useRef } from 'preact/compat'
import { useDispatch, useSelector } from 'react-redux'
import JSEncrypt from 'jsencrypt'
import PropTypes from 'prop-types'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import { hideRecaptchaGlobal } from '../../../helpers/recaptcha'
import { billingAddressSelector } from '../../../redux/slice/billingAddressSlice'
import { setAddCard, cardsSelector } from '../../../redux/slice/cardsSlice'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import {
  paymentResetCardEncryptedData,
  paymentUpdate,
  paymentSelector,
} from '../../../redux/slice/paymentSlice'
import { prePaymentSelector } from '../../../redux/slice/prePaymentSlice'
import Loader from '../../Common/Loader'
import CardErrors from './CardErrors'
import { CARDINAL_STATUSES } from '../../../constants/cardinal'
import useTouchedFields from '../../../hooks/useTouchedFields'
import usePayment from '../../../hooks/usePayment'
import './styles.scss'
import { config } from '../../../config'
import { useTranslations } from '../../../hooks/useTranslations'
import {
  dispatchMFEAdobeEvent,
  dispatchMFEEvent,
  dispatchPaymentDetailsIframeEvent,
  dispatchPayRequest,
} from '../../../helpers/events'
import {
  selectIsEvervaultEnabled,
  selectIsPciFormEnabled,
  selectIsCardFormComplete,
  selectHasCardError,
  selectCardFormValues,
} from '../../../redux/selectors/evervault'
import { DataDog } from '../../../datadog'
import useFailureRedirect from '../../../hooks/useFailureRedirect'
import CardinalDDC from '../../CardinalDDC'
import InfoMessage from '../../Common/InfoMessage'
import {
  formatAsCurrency,
  loadScript,
  redirect,
  sprintf,
} from '../../../helpers/common'
import LinkButton from '../../Common/LinkButton/index'
import { cardinalCruiseSelector } from '../../../redux/slice/cardinalCruiseSlice'
import {
  authoriseError,
  clearAuthoriseData,
  paymentResponseSelector,
  updatePaymentPending,
} from '../../../redux/slice/paymentResponseSlice'
import { reencryptCard } from '../../../api/payments'
import {
  buildReencryptPayload,
  isProhibitedGiftCard,
  isValidCardBin,
} from '../../../helpers/evervault'
import {
  ERROR_RESPONSES,
  SUCCESS_RESPONSES,
} from '../../../constants/serverResponses'
import {
  transactionDetailsSelector,
  transactionDetailsUpdate,
} from '../../../redux/slice/transactionDetailsSlice'
import {
  getCountryCode,
  applyHookBillingAddressOverride,
} from '../../../helpers/billingAddress'

const Card = ({
  watch,
  errors,
  handleSubmit,
  showPaymentConfirmation,
  isGoBackButtonVisible,
  loaderImage,
  iframeRef,
  getValues,
}) => {
  const dispatch = useDispatch()
  const recaptchaRef = useRef()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const billingAddress = useSelector(billingAddressSelector)
  const paymentResponse = useSelector(paymentResponseSelector)
  const pageState = useSelector(pageStateSelector)
  const payment = useSelector(paymentSelector)
  const cards = useSelector(cardsSelector)
  const cardinalCruise = useSelector(cardinalCruiseSelector)
  const isEvervaultEnabled = useSelector(selectIsEvervaultEnabled)
  // Evervault > PCI > default: false whenever Evervault is active.
  const isPciFormEnabled = useSelector(selectIsPciFormEnabled)
  const isCardFormComplete = useSelector(selectIsCardFormComplete)
  const hasCardError = useSelector(selectHasCardError)
  const cardFormValues = useSelector(selectCardFormValues)
  const { savedCards, selectedCard, isAddCard } = cards
  const { isPaymentPending } = paymentResponse
  const { cardinalStatus } = cardinalCruise
  // TODO: leave this only for OmnioCard component
  const prePayment = useSelector(prePaymentSelector)

  useFailureRedirect()

  const [paymentBlockedError, setPaymentBlockedError] = useState('')
  // Evervault form: prohibited gift card (Evervault can't block it itself)
  const [cardNotAcceptedError, setCardNotAcceptedError] = useState('')
  const [isCardError, setIsCardError] = useState(false)
  const l10n = useTranslations()

  const formatAmount = formatAsCurrency(
    transactionDetails.currency,
    transactionDetails.culture,
  )

  const currentAmount = formatAmount(
    transactionDetails?.overrideAmount
      ? transactionDetails.overrideAmount
      : transactionDetails.amount,
  )

  const setIsAddCard = state => {
    dispatch(setAddCard(state))
  }

  const { proceedPayment, isPayButtonVisible } = usePayment(iframeRef)

  const { hasBeenTouchedAfterSubmit, setSubmittedData, setTouchedAfterSubmit } =
    useTouchedFields(watch, isPciFormEnabled)

  const [iframeHasBeenTouchedAfterSubmit, setIframeHasBeenTouchedAfterSubmit] =
    useState(false)

  const [formErrors, setFormErrors] = useState({})

  const checkIsTermsAndConditionsEnabled = () => {
    if (pageState.isPaymentBlocked) {
      setPaymentBlockedError(l10n.t('payment-btn-error'))
      return false
    }
    setPaymentBlockedError('')

    return true
  }

  const handlePCIIframeMessage = e => {
    const type = e?.data?.type

    if (
      type === 'MFEFormPropsUpdate' &&
      e.data.data?.hasBeenTouchedAfterSubmit
    ) {
      setIframeHasBeenTouchedAfterSubmit(e.data.data?.hasBeenTouchedAfterSubmit)
    } else if (type === 'MFEFormPropsUpdate' && e.data.data?.errors) {
      setFormErrors(JSON.parse(e.data.data.errors))
    } else if (type === 'termsAndConditionsDisabled') {
      checkIsTermsAndConditionsEnabled()
    } else if (type === 'MFEStartCardinalProcess') {
      const cardBin = e?.data?.data?.cardBin

      dispatch(
        paymentUpdate({
          cardNumber: cardBin,
          cardBin: selectedCard?.bin || cardBin,
        }),
      )

      proceedPayment(
        () => {
          dispatch(paymentResetCardEncryptedData())
          setIsAddCard(false)

          dispatchPaymentDetailsIframeEvent(
            {
              type: 'PaymentsMFESetTouchedAfterSubmit',
              data: {
                value: false,
              },
            },
            iframeRef?.current?.contentWindow,
          )
        },
        () => {
          dispatchPaymentDetailsIframeEvent(
            {
              type: 'PaymentsMFESetTouchedAfterSubmit',
              data: {
                value: false,
              },
            },
            iframeRef?.current?.contentWindow,
          )
        },
      )
    }
  }

  useEffect(() => {
    window.addEventListener('message', handlePCIIframeMessage)

    return () => window.removeEventListener('message', handlePCIIframeMessage)
  }, [pageState.isPaymentBlocked, billingAddress])

  const errorFields = isPciFormEnabled
    ? Object.keys(formErrors)
    : Object.keys(errors)

  const rejectProhibitedGiftCard = bin => {
    if (!isProhibitedGiftCard(bin, transactionDetails.prohibitedCardBrands)) {
      return false
    }

    DataDog.info('Evervault: prohibited gift card blocked before payment')
    setCardNotAcceptedError(
      l10n.t('Sorry, this card is not accepted, please use a different card'),
    )
    return true
  }

  // A new card number clears the "not accepted" error
  useEffect(() => {
    setCardNotAcceptedError('')
  }, [cardFormValues?.card?.bin])

  const submitEvervault = () => {
    dispatchPayRequest()

    if (!isCardFormComplete || hasCardError) {
      setTouchedAfterSubmit(true)
      return
    }

    // Same rule as the manual form: blocked before anything reaches the BE
    if (rejectProhibitedGiftCard(cardFormValues?.card?.bin)) {
      return
    }

    if (!checkIsTermsAndConditionsEnabled()) {
      return
    }

    // BillingAddress/email/cardholder fields remain outside the iframe and are
    // still validated via react-hook-form, mirroring the manual/apexx branch.
    handleSubmit(
      data => {
        const { card } = cardFormValues

        dispatch(
          paymentUpdate({
            // Name is captured inside the Evervault iframe, not react-hook-form.
            cardholderName: card?.name || undefined,
            saveCard: data.saveCard,
            notes: data.notes || undefined,
          }),
        )

        dispatch(
          transactionDetailsUpdate({
            billingAddress: {
              addressLine1: data.addressLine1,
              addressLine2: data.addressLine2,
              city: data.city,
              email: data.email,
              country: getCountryCode(data.country, l10n),
              postalCode: data.postalCode,
              state: data?.state || undefined,
            },
          }),
        )

        dispatch(clearAuthoriseData())

        // Re-encrypt runs after onAuthorising, so a cancelled hook never
        // sends the card to the back end.
        const continuePayment = async overrideValues => {
          const reencrypted = await reencryptEvervaultCard(card)
          if (!reencrypted) return

          dispatch(
            paymentUpdate({
              // Apexx token from /reencrypt — same contract as the JSEncrypt path.
              cardEncryptedData: reencrypted.cardEncryptedData,
              cardBin: reencrypted.cardBin,
              // The saga passes `cardNumber` to Cardinal 'bin.process'.
              cardNumber: reencrypted.cardBin,
            }),
          )

          proceedPayment(
            () => {
              window.tuiPaymentsMFEInstance?.onAuthorised?.()
              dispatch(paymentResetCardEncryptedData())
              setIsAddCard(false)
              setTouchedAfterSubmit(false)
            },
            () => {
              setTouchedAfterSubmit(false)
            },
            overrideValues,
          )
        }

        const mfeElement = window.tuiPaymentsMFEInstance

        if (mfeElement?.onAuthorising) {
          DataDog.info(`onAuthorising hook has been enabled`)

          mfeElement
            .onAuthorising?.({
              paymentMethod: PAYMENT_METHOD_TYPES.Card,
              billingAddress,
            })
            .then(overrideValues => {
              continuePayment(overrideValues)
              DataDog.info(`onAuthorising Promise resolved`)
            })
            .catch(() => {
              DataDog.info(`onAuthorising Promise rejected`)
              console.log('onAuthorising Promise rejected')
            })
        } else {
          continuePayment()
        }
      },
      () => checkIsTermsAndConditionsEnabled(),
    )()
  }

  /**
   * Exchange the Evervault card (`ev:` number/cvc) for the Apexx token via
   * `/transaction/card/reencrypt`. Shows the loader while pending. On failure
   * it raises a payment error (retryable unless the BE says otherwise) and
   * returns null, leaving the form filled so the user can retry.
   */
  const reencryptEvervaultCard = async card => {
    dispatch(updatePaymentPending(true))
    // Drop a token from a previous attempt so it can never be re-used.
    dispatch(
      paymentUpdate({ cardEncryptedData: null, cardBin: null, cardNumber: null }),
    )

    try {
      const response = await reencryptCard(buildReencryptPayload(card))
      const { responseStatus, responseData } = response || {}

      const isSuccess =
        responseStatus === SUCCESS_RESPONSES.OK &&
        responseData?.cardEncryptedData

      if (isSuccess && isValidCardBin(responseData.cardBin)) {
        // Fallback when Evervault gave no BIN up front
        if (rejectProhibitedGiftCard(responseData.cardBin)) {
          dispatch(updatePaymentPending(false))
          return null
        }
        return responseData
      }

      // Log status/code only — never the card or the token.
      if (isSuccess) {
        // Without a BIN, Cardinal bin.process/DDC and authorise can't run
        DataDog.error('Evervault card re-encryption returned no valid BIN', {
          responseStatus,
          cardBinLength: responseData.cardBin?.length ?? 0,
        })
      } else {
        DataDog.error('Evervault card re-encryption failed', {
          responseStatus,
          errorCode: responseData?.errorCode,
        })
      }
      dispatch(
        authoriseError({
          responseStatus: responseStatus ?? ERROR_RESPONSES.SERVER_ERROR,
          errorCode: responseData?.errorCode,
          errorMessage: responseData?.errorMessage,
          isRetryable: responseData?.isRetryable ?? true,
        }),
      )
    } catch (e) {
      DataDog.error('Evervault card re-encryption threw', {
        error: e?.message,
      })
      dispatch(
        authoriseError({
          responseStatus: ERROR_RESPONSES.SERVER_ERROR,
          isRetryable: true,
        }),
      )
    }

    // Let CardErrors show the payment error (it hides while "touched").
    setTouchedAfterSubmit(false)
    return null
  }

  const cardDataEncryption = () => {
    if (isEvervaultEnabled) {
      return submitEvervault()
    }

    if (isPciFormEnabled) {
      if (!pageState.isPaymentBlocked) {
        checkIsTermsAndConditionsEnabled()
      }

      dispatchPaymentDetailsIframeEvent(
        {
          type: 'PaymentsMFEPay',
          data: {
            isTermsAndConditionsEnabled: pageState.isPaymentBlocked,
            notes: getValues('notes'),
          },
        },
        iframeRef?.current?.contentWindow,
      )

      return
    }

    handleSubmit(
      data => {
        if (!checkIsTermsAndConditionsEnabled()) {
          return
        }

        const { cardholderName } = data
        const cardNumber = data.number?.replace(/\s/g, '')
        const expirationMonth = `0${data.expirationMonth}`.slice(-2)
        const expirationYear = `0${data.expirationYear}`.slice(-2)
        const normalizedCardholderName = /[’']/.test(cardholderName)
          ? cardholderName
              .replace(/\\u0027/g, "'")
              .replace(/\\u2019/g, '’')
              .replace(/\u2019/g, "'")
          : cardholderName
        DataDog.setCardIdentification(
          cardNumber,
          cardholderName,
          expirationMonth,
          expirationYear,
        )

        const Json = {
          card_holder_name: normalizedCardholderName,
          card_number: cardNumber,
          expiry_month: expirationMonth,
          expiry_year: expirationYear,
          cvv: data.cvv,
        }

        setSubmittedData(data)

        const publicKeyString = config.apexxPublicKey

        if (publicKeyString.length !== 0) {
          const encrypt = new JSEncrypt()
          encrypt.setPublicKey(publicKeyString)
          const cardEncryptedData = encrypt.encrypt(JSON.stringify(Json))

          dispatch(
            paymentUpdate({
              cardNumber: data.number,
              cardBin: data.number.replace(/\s+/g, '').substring(0, 6),
              saveCard: data.saveCard,
              cardholderName: normalizedCardholderName,
              notes: data.notes || undefined,
              cardEncryptedData,
            }),
          )

          // Build the billing address from the card form, then re-apply the
          // accumulated hook override on top so tenant overrides from
          // onInitialised / onMethodSelecting / onMethodSelected are not lost
          // (override wins over the form values at authorization time).
          const effectiveBillingAddress = applyHookBillingAddressOverride(
            {
              addressLine1: data.addressLine1,
              addressLine2: data.addressLine2,
              city: data.city,
              email: data.email,
              country: getCountryCode(data.country, l10n),
              postalCode: data.postalCode,
              state: data?.state || undefined,
            },
            transactionDetails.hookBillingAddressOverride,
          )

          dispatch(
            transactionDetailsUpdate({
              billingAddress: effectiveBillingAddress,
            }),
          )

          dispatch(clearAuthoriseData())

          const continuePayment = overrideValues => {
            proceedPayment(
              () => {
                window.tuiPaymentsMFEInstance?.onAuthorised?.()
                dispatch(paymentResetCardEncryptedData())
                setIsAddCard(false)
                setTouchedAfterSubmit(false)
              },
              () => {
                setTouchedAfterSubmit(false)
              },
              overrideValues,
            )
          }

          const mfeElement = window.tuiPaymentsMFEInstance

          if (mfeElement?.onAuthorising) {
            DataDog.info(`onAuthorising hook has been enabled`)

            mfeElement
              .onAuthorising?.({
                paymentMethod: PAYMENT_METHOD_TYPES.Card,
                billingAddress: effectiveBillingAddress,
              })
              .then(overrideValues => {
                continuePayment(overrideValues)
                DataDog.info(`onAuthorising Promise resolved`)
              })
              .catch(() => {
                DataDog.info(`onAuthorising Promise rejected`)
                console.log('onAuthorising Promise rejected')
              })
          } else {
            continuePayment()
          }
        }
      },
      () => checkIsTermsAndConditionsEnabled(),
    )()
  }

  useEffect(() => {
    if (!pageState.isPaymentBlocked) {
      setPaymentBlockedError('')
    }
  }, [pageState.isPaymentBlocked])

  useEffect(() => {
    const isCardNotSelected = savedCards.length && !selectedCard && !isAddCard

    if (!isCardNotSelected) {
      setIsCardError(false)
    }
  }, [selectedCard, isAddCard])

  const handlePayment = () => {
    DataDog.info('Card payment button clicked')
    dispatchMFEEvent('tui-button.payment-btn-clicked', {
      paymentMethod: 'Card',
    })
    dispatchMFEAdobeEvent('Pay-Card')

    const isCardNotSelected = savedCards.length && !selectedCard && !isAddCard

    if (isCardNotSelected) {
      setIsCardError(true)
      return
    }
    setIsCardError(false)

    if (selectedCard) {
      if (isPciFormEnabled) {
        dispatchPaymentDetailsIframeEvent(
          {
            type: 'PaymentsMFEPaySelectedCard',
            data: {
              selectedCard,
              notes: getValues('notes'),
            },
          },
          iframeRef?.current?.contentWindow,
        )

        return
      }

      handleSubmit(({ cvv, saveCard, notes }) => {
        DataDog.setCardIdentification(
          selectedCard.maskedCardNumber,
          selectedCard.cardholderName,
          selectedCard.expiryMonth,
          selectedCard.expiryYear,
        )

        dispatch(
          paymentUpdate({
            cardBin: selectedCard.bin,
            cvv,
            saveCard,
            notes: notes || undefined,
          }),
        )
        dispatch(clearAuthoriseData())

        const continuePayment = () => {
          proceedPayment(
            () => {
              window.tuiPaymentsMFEInstance?.onAuthorised?.()
              dispatch(paymentResetCardEncryptedData())
              setIsAddCard(false)
            },
            () => {
              setTouchedAfterSubmit(false)
            },
          )
        }

        const mfeElement = window.tuiPaymentsMFEInstance

        if (mfeElement?.onAuthorising) {
          DataDog.info(`onAuthorising hook has been enabled`)

          mfeElement
            .onAuthorising?.({ paymentMethod: PAYMENT_METHOD_TYPES.Card })
            .then(() => {
              continuePayment()
              DataDog.info(`onAuthorising Promise resolved`)
            })
            .catch(() => {
              DataDog.info(`onAuthorising Promise rejected`)
              console.log('onAuthorising Promise rejected')
            })
        } else {
          continuePayment()
        }
      })()

      return
    }

    cardDataEncryption()
  }

  useEffect(() => {
    if (
      transactionDetails.recaptcha.isEnabled &&
      !isPciFormEnabled
    ) {
      loadScript(
        `https://www.google.com/recaptcha/enterprise.js?render=${config.recaptchaSiteKey}`,
        () => {},
        () => {
          console.error("Can't load recaptcha script")
        },
        true,
      )
    }

    if (transactionDetails.recaptcha.isEnabled) {
      hideRecaptchaGlobal()
    }
  }, [transactionDetails.recaptcha.isEnabled])

  return (
    <>
      {cardinalStatus === CARDINAL_STATUSES.success ? (
        <div className="success">
          <div>Processing...</div>
          <Loader />
        </div>
      ) : (
        <>
          <form
            data-apexx="payment-form"
            className="payment-form"
            data-testid="payment-form"
          >
            {showPaymentConfirmation && (
              <InfoMessage
                message={sprintf(
                  l10n.t(`You're about to pay %s`),
                  currentAmount,
                )}
                data-testid="card-info-message"
                className="card-info-message"
              />
            )}
            <CardErrors
              hasBeenTouchedAfterSubmit={
                isPciFormEnabled
                  ? iframeHasBeenTouchedAfterSubmit
                  : hasBeenTouchedAfterSubmit
              }
              paymentBlockedError={paymentBlockedError}
              cardNotAcceptedError={cardNotAcceptedError}
              isCardError={isCardError}
              errorFields={errorFields}
              iframeRef={iframeRef}
            />

            <CardinalDDC />

            {isPaymentPending ? (
              <Loader
                loaderImage={loaderImage || transactionDetails.loaderOption}
              />
            ) : (
              isPayButtonVisible && (
                <div className="payment-form__payment-button">
                  {isGoBackButtonVisible && (
                    <LinkButton
                      data-testid="go-back-button"
                      title={l10n.t('Go Back')}
                      onClick={() => redirect(transactionDetails.returnUrl)}
                      className="go-back-button"
                    />
                  )}
                  <div className="button-container">
                    {transactionDetails.recaptcha.isEnabled && (
                      <div className="recaptcha-button">
                        {l10n.t('RECAPTCHA_PRE')}
                        <span> </span>
                        <a
                          href="https://policies.google.com/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {l10n.t('RECAPTCHA_PRIVACY')}
                        </a>
                        {l10n.t('RECAPTCHA_MID')}
                        <a
                          href="https://policies.google.com/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {l10n.t('RECAPTCHA_TERMS')}
                        </a>
                        .
                      </div>
                    )}
                    <input
                      type="button"
                      value={l10n.t('Pay Now')}
                      className="button primary medium card-payment-btn"
                      onClick={handlePayment}
                      data-testid="payment-button"
                      disabled={
                        prePayment.hasError || prePayment.isRequestPending
                      }
                    />
                  </div>
                </div>
              )
            )}
          </form>
        </>
      )}
    </>
  )
}

Card.propTypes = {
  watch: PropTypes.func,
  errors: PropTypes.object,
  handleSubmit: PropTypes.func,
  showPaymentConfirmation: PropTypes.bool,
  isGoBackButtonVisible: PropTypes.bool,
  loaderImage: PropTypes.string,
}

export default Card
